import React, { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import { useALCContext, ALC_STATES } from '../context/ALCContext';
import { alcService } from '../services/alcService';
import ALCStepper from './ALCStepper';
import store from '../../../../store/store';
import { addPendingSync } from '../../../../store/ALCSlice';
import { saveALCAttachmentToIndexedDB } from '../services/ALCOfflineService';
import { getAccessToken } from '../../../../Services/getAccessToken';
import { useNavigate } from 'react-router-dom';
import { ALC_CHECKLIST_SEED_DATA, MAX_ALC_SCORE } from '../constants/alcSeedData';

const ALCQAReverify: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { state, dispatch } = useALCContext();
    const navigate = useNavigate();

    const [allCheckpoints, setAllCheckpoints] = useState<any[]>([]);
    const [failedCheckpoints, setFailedCheckpoints] = useState<any[]>([]);
    const [reverifyScores, setReverifyScores] = useState<Record<string, string>>({});
    const [reverifyRemarks, setReverifyRemarks] = useState<Record<string, string>>({});
    const [reverifyFiles, setReverifyFiles] = useState<Record<string, File[]>>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        const loadCheckpoints = async () => {
            if (!state.currentTourId) return;
            setIsLoading(true);
            try {
                const checkpoints = await alcService.getCheckpoints(state.currentTourId);
                setAllCheckpoints(checkpoints);

                // Filter items that were marked as Not Okay or Partial / Non-Compliant
                const failed = checkpoints.filter((c: any) =>
                    c.cr3ea_status === "Not Okay" ||
                    (c.cr3ea_defectcategory && (
                        c.cr3ea_defectcategory.includes("00") ||
                        c.cr3ea_defectcategory.includes("01") ||
                        c.cr3ea_defectcategory.includes("Non-Compliant") ||
                        c.cr3ea_defectcategory.includes("Partial")
                    ))
                );

                setFailedCheckpoints(failed);
                dispatch({ type: 'SET_FAILED_CHECKPOINTS', payload: failed });

            } catch (error) {
                console.error("Failed to load checkpoints for QA re-verification:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (navigator.onLine) {
            loadCheckpoints();
        }
    }, [state.currentTourId, dispatch]);

    const handleScoreChange = (rowKey: string, value: string) => {
        setReverifyScores(prev => ({ ...prev, [rowKey]: value }));
    };

    const handleRemarksChange = (rowKey: string, value: string) => {
        setReverifyRemarks(prev => ({ ...prev, [rowKey]: value }));
    };

    const handleFileChange = (rowKey: string, files: FileList | null) => {
        if (!files || files.length === 0) return;

        const selectedFiles = Array.from(files);
        const validImages = selectedFiles.filter(file =>
            file.type.startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(file.name)
        );

        if (validImages.length < selectedFiles.length) {
            alert(`${selectedFiles.length - validImages.length} non-image file(s) were ignored. Only images are allowed.`);
        }

        setReverifyFiles(prev => {
            const current = prev[rowKey] || [];
            const filtered = validImages.filter(f => !current.some(cf => cf.name === f.name && cf.size === f.size));
            return {
                ...prev,
                [rowKey]: [...current, ...filtered]
            };
        });
    };

    const handleRemoveFile = (rowKey: string, fileIdx: number) => {
        setReverifyFiles(prev => {
            const current = prev[rowKey] || [];
            const updated = current.filter((_, idx) => idx !== fileIdx);
            if (updated.length === 0) {
                const copy = { ...prev };
                delete copy[rowKey];
                return copy;
            }
            return { ...prev, [rowKey]: updated };
        });
    };

    const handleSubmit = async () => {
        if (!state.currentTourId) {
            alert("No active session ID found.");
            return;
        }

        // Validate re-verification inputs
        let incompleteCount = 0;
        let missingFilesCount = 0;
        let anyFailedAgain = false;

        for (let i = 0; i < failedCheckpoints.length; i++) {
            const cp = failedCheckpoints[i];
            const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;

            // Check if production has submitted action remarks
            const prodRemark = cp.cr3ea_productionremarks || "";
            const isReady = prodRemark && prodRemark.trim().startsWith("Action:");
            if (!isReady) continue; // Waiting for production, skip active validation

            const score = reverifyScores[rowKey];
            if (!score) {
                incompleteCount++;
            }

            const isNonCompliant = score && (
                score.includes("Non-Compliant") ||
                score.includes("(0)") ||
                score === "00"
            );

            const isPartial = score && (
                score.includes("Partial") ||
                score.includes("(1)") ||
                score === "01"
            );

            if (isNonCompliant || isPartial) {
                anyFailedAgain = true;
            }

            const files = reverifyFiles[rowKey] || [];
            if (isNonCompliant && files.length === 0) {
                missingFilesCount++;
            }
        }

        if (incompleteCount > 0 || missingFilesCount > 0) {
            let msg = "";
            if (incompleteCount > 0) {
                msg += `Please select re-verification score for ${incompleteCount} actioned checkpoint(s).\n`;
            }
            if (missingFilesCount > 0) {
                msg += `Uploading a proof image is mandatory for ${missingFilesCount} Non-Compliant checkpoint(s).`;
            }
            alert(msg.trim());
            return;
        }

        // Calculate new overall score across all 46 checkpoints
        let totalMaxScore = MAX_ALC_SCORE;
        let totalObtainedScore = 0;
        let hasCriticalFailure = false;
        const failedCriticalItems: any[] = [];
        let hasUnresolvedItems = anyFailedAgain;

        // Build updated checkpoint records
        const recordsToUpdate: any[] = [];

        failedCheckpoints.forEach(cp => {
            const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;
            const prodRemark = cp.cr3ea_productionremarks || "";
            const isReady = prodRemark && prodRemark.trim().startsWith("Action:");

            if (isReady && reverifyScores[rowKey]) {
                const scoreText = reverifyScores[rowKey];
                const remarks = (reverifyRemarks[rowKey] || "").trim();
                const files = reverifyFiles[rowKey] || [];

                let numScore = 2;
                let status = "OK";

                if (scoreText.includes("(0)") || scoreText === "00" || scoreText.includes("Non-Compliant")) {
                    numScore = 0;
                    status = "Not Okay";
                } else if (scoreText.includes("(1)") || scoreText === "01" || scoreText.includes("Partial")) {
                    numScore = 1;
                    status = "Not Okay";
                }

                recordsToUpdate.push({
                    cp,
                    rowKey,
                    numScore,
                    status,
                    scoreText,
                    remarks,
                    files
                });
            } else {
                // If not re-verified yet, keep previous score
                hasUnresolvedItems = true;
            }
        });

        // Loop all checkpoints to sum scores and check Critical Gate items
        allCheckpoints.forEach(cp => {
            const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;
            const updateItem = recordsToUpdate.find(u => u.rowKey === rowKey);

            let numericScore = 2;
            let finalScoreText = cp.cr3ea_defectcategory || "Compliant (2)";

            if (updateItem) {
                numericScore = updateItem.numScore;
                finalScoreText = updateItem.scoreText;
            } else {
                if (finalScoreText.includes("(0)") || finalScoreText === "00" || finalScoreText.includes("Non-Compliant")) {
                    numericScore = 0;
                } else if (finalScoreText.includes("(1)") || finalScoreText === "01" || finalScoreText.includes("Partial")) {
                    numericScore = 1;
                }
            }

            totalObtainedScore += numericScore;

            // Check if critical
            const cleanCrit = (cp.cr3ea_criteria || "").replace(/CRITICAL(?: GATE)?/gi, "").trim().toLowerCase();
            const seedMatch = ALC_CHECKLIST_SEED_DATA.find(s => {
                const sTitle = s.title.trim().toLowerCase();
                return sTitle === cleanCrit || cleanCrit.includes(sTitle) || sTitle.includes(cleanCrit);
            });

            const isCritical = seedMatch ? seedMatch.isCritical : false;

            if (isCritical && (numericScore === 0 || numericScore === 1)) {
                hasCriticalFailure = true;
                failedCriticalItems.push({
                    criteria: cp.cr3ea_criteria || "Critical Item",
                    score: numericScore,
                    area: cp.cr3ea_area || ""
                });
            }
        });

        const overallPercentRaw = (totalObtainedScore / totalMaxScore) * 100;
        const overallPercent = overallPercentRaw.toFixed(2);
        let isPass = (parseFloat(overallPercent) >= 80) && !hasCriticalFailure;

        let statusText = "Completed";
        const isPreviousDay = state.isPreviousDay;

        if (isPreviousDay) {
            isPass = false;
            statusText = "Closed - Expired";
        } else if (hasCriticalFailure) {
            isPass = false;
            statusText = "Failed - Pending Production";
        } else if (hasUnresolvedItems) {
            statusText = isPass ? "Success - Pending Production" : "Failed - Pending Production";
        }

        const dbStatusValue = statusText === "Success - Pending Production" ? "Failed - Pending Production" : statusText;
        const isLineClearVal = isPass && !isPreviousDay;

        const saveOffline = async () => {
            const syncPayloads: any[] = [];

            for (const item of recordsToUpdate) {
                const files = item.files;
                let reverifyVal = item.remarks;
                if (files.length > 0) {
                    reverifyVal = reverifyVal
                        ? `${reverifyVal} | File: ${files.map((f: File) => f.name).join(', ')}`
                        : ` | File: ${files.map((f: File) => f.name).join(', ')}`;

                    for (const f of files) {
                        await saveALCAttachmentToIndexedDB(
                            state.currentTourId!,
                            item.cp.cr3ea_rajpura_alcsid || item.rowKey,
                            item.cp.cr3ea_area,
                            reverifyVal,
                            f
                        );
                    }
                }

                const baseRemark = item.cp.cr3ea_defectremarks || "";
                const finalRemarks = reverifyVal
                    ? (baseRemark ? `${baseRemark} | Re-verified: ${reverifyVal}` : `Re-verified: ${reverifyVal}`)
                    : baseRemark;

                const rowRecord: any = {
                    cr3ea_rajpura_alcsid: item.cp.cr3ea_rajpura_alcsid || item.rowKey,
                    cr3ea_status: item.status,
                    cr3ea_cycle: "Cycle-2",
                    cr3ea_defectcategory: item.scoreText,
                    cr3ea_defectremarks: finalRemarks
                };

                if (item.status === "Not Okay") {
                    rowRecord.cr3ea_productionremarks = "";
                }

                syncPayloads.push({
                    id: `${Date.now()}_checkrow_${item.rowKey}`,
                    type: 'CHECKLIST_ROW',
                    method: 'PATCH',
                    payload: rowRecord,
                    createdAt: Date.now()
                });
            }

            syncPayloads.push({
                id: `${Date.now()}_session_${state.currentTourId}`,
                type: 'SESSION_UPDATE',
                method: 'PATCH',
                payload: {
                    cr3ea_prod_qualitytourid: state.currentTourId,
                    cr3ea_status: dbStatusValue,
                    cr3ea_processstatus: statusText,
                    cr3ea_overall_score: String(overallPercent),
                    cr3ea_checklist_result: isPreviousDay ? "Expired" : (isPass ? "Pass" : "Fail"),
                    cr3ea_islineclear: isLineClearVal
                },
                createdAt: Date.now()
            });

            store.dispatch(addPendingSync(syncPayloads));

            if (isPreviousDay) {
                alert(`Observations Submitted Offline! Since this is a previous day's observation, the session has been closed as Expired.`);
            } else if (statusText === "Success - Pending Production") {
                alert(`Re-verification complete offline. Current score: ${overallPercent}% (Success). Some checkpoints are still pending.`);
            } else if (statusText === "Completed") {
                alert(`ALC Re-Verification Cleared Successfully! Overall Score: ${overallPercent}%. Saved offline.`);
            } else {
                alert(`Re-Verification failed. Current score: ${overallPercent}%. Saved offline.`);
            }

            navigate('/home');
        };

        try {
            dispatch({ type: 'SET_LOADING', payload: true });

            if (!navigator.onLine) {
                await saveOffline();
                return;
            }

            const tokenResult = await getAccessToken(undefined, undefined, true);
            const accessToken = tokenResult?.token;

            // 1. Upload proof files & update rows
            for (const item of recordsToUpdate) {
                const files = item.files;
                let uploadedNames: string[] = [];

                if (files.length > 0) {
                    const uploadPromises = files.map((file: File) =>
                        alcService.uploadCorrectiveActionFile(
                            file,
                            state.currentTourId!,
                            item.cp.cr3ea_area,
                            item.cp.cr3ea_rajpura_alcsid || item.rowKey,
                            item.remarks,
                            accessToken,
                            instance,
                            accounts[0]
                        ).then((url: string) => {
                            if (url) return url.substring(url.lastIndexOf("/") + 1);
                            return file.name;
                        })
                    );
                    uploadedNames = await Promise.all(uploadPromises);
                }

                let reverifyVal = item.remarks;
                if (uploadedNames.length > 0) {
                    reverifyVal = reverifyVal
                        ? `${reverifyVal} | File: ${uploadedNames.join(', ')}`
                        : ` | File: ${uploadedNames.join(', ')}`;
                }

                const baseRemark = item.cp.cr3ea_defectremarks || "";
                const finalRemarks = reverifyVal
                    ? (baseRemark ? `${baseRemark} | Re-verified: ${reverifyVal}` : `Re-verified: ${reverifyVal}`)
                    : baseRemark;

                const rowRecord: any = {
                    cr3ea_rajpura_alcsid: item.cp.cr3ea_rajpura_alcsid,
                    cr3ea_status: item.status,
                    cr3ea_cycle: "Cycle-2",
                    cr3ea_defectcategory: item.scoreText,
                    cr3ea_defectremarks: finalRemarks
                };

                if (item.status === "Not Okay") {
                    rowRecord.cr3ea_productionremarks = "";
                }

                await alcService.saveChecklistRow(rowRecord);
            }

            // 2. Update Tour Session status in Dataverse
            await alcService.saveSession({
                cr3ea_prod_qualitytourid: state.currentTourId,
                cr3ea_status: dbStatusValue,
                cr3ea_processstatus: statusText,
                cr3ea_overall_score: String(overallPercent),
                cr3ea_checklist_result: isPreviousDay ? "Expired" : (isPass ? "Pass" : "Fail"),
                cr3ea_islineclear: isLineClearVal
            });

            // Alerts
            if (isPreviousDay) {
                alert(`Observations Submitted Successfully! Since this is a previous day's observation, the session has been closed as Expired without line clearance.`);
            } else if (statusText === "Success - Pending Production") {
                alert(`Re-verification complete. Current score is: ${overallPercent}% (Success). However, since there are still failing checkpoints, assigning back to Production.`);
            } else if (statusText === "Completed") {
                alert(`ALC Re-Verification Cleared Successfully! Overall Score: ${overallPercent}%`);
            } else {
                alert(`Re-Verification failed. Current score is: ${overallPercent}%. Some checkpoints are still non-compliant. Returning to production.`);
            }

            if (statusText === "Completed" || isPreviousDay) {
                dispatch({ type: 'SET_STATE', payload: ALC_STATES.SUMMARY });
            } else {
                navigate('/home');
            }

        } catch (error: any) {
            console.error("Error submitting re-verification:", error);
            if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('Network Error'))) {
                console.warn("Network error during submit, falling back to offline save:", error);
                await saveOffline();
            } else {
                alert("Dataverse Error: Failed to submit re-verification - " + error.message);
            }
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <div id="section-reverification">
            <ALCStepper />

            {/* Warning banner */}
            {state.isPreviousDay && (
                <div
                    style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fef3c7',
                        borderLeft: '4px solid #f59e0b',
                        padding: '12px 16px',
                        marginBottom: '15px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fa fa-exclamation-triangle" style={{ fontSize: '16px' }}></i>
                    <span>
                        <strong>Previous Day Tour:</strong> This observation was recorded on a previous day. Re-verification results will remain archived under <strong>Closed - Expired</strong> without granting line clearance.
                    </span>
                </div>
            )}

            <div className="bs-card" style={{ marginBottom: '20px', border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden' }}>
                <div className="bs-card-header" style={{ backgroundColor: '#f1f5f9', padding: '14px 20px', borderBottom: '1px solid #cbd5e1' }}>
                    <h4 className="bs-card-title text-primary" style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                        <i className="fa fa-check-square-o"></i> QA Re-Verification of Corrective Actions (Cycle-2)
                    </h4>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                        Review corrective actions taken by Production and re-evaluate compliance for failed checkpoints.
                    </p>
                </div>

                <div className="bs-card-body" style={{ padding: '0', overflowX: 'auto' }}>
                    {isLoading ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                            <div className="spinner-border spinner-border-sm text-primary" role="status"></div> Loading checkpoints for re-verification...
                        </div>
                    ) : failedCheckpoints.length === 0 ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: '#16a34a', fontWeight: 'bold' }}>
                            <i className="fa fa-check-circle" style={{ fontSize: '20px', marginRight: '6px' }}></i>
                            All checkpoints have been cleared!
                        </div>
                    ) : (
                        <table className="bs-table" style={{ width: '100%', minWidth: '850px', borderCollapse: 'collapse', textAlign: 'center', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569' }}>
                                    <th style={{ width: '5%', padding: '10px' }}>Sr No.</th>
                                    <th style={{ width: '40%', padding: '10px', textAlign: 'left' }}>Checkpoint &amp; Production Action</th>
                                    <th style={{ width: '17%', padding: '10px' }}>Re-Verify Score</th>
                                    <th style={{ width: '20%', padding: '10px' }}>Verification Remarks</th>
                                    <th style={{ width: '18%', padding: '10px' }}>Proof Photo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {failedCheckpoints.map((cp, idx) => {
                                    const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;
                                    const prodRemark = cp.cr3ea_productionremarks || "";
                                    const isReady = prodRemark && prodRemark.trim().startsWith("Action:");
                                    const scoreVal = reverifyScores[rowKey] || "";
                                    const remarksVal = reverifyRemarks[rowKey] || "";
                                    const files = reverifyFiles[rowKey] || [];

                                    // Parse QA initial defect remarks
                                    const defectRemarks = cp.cr3ea_defectremarks || "";
                                    let cleanQaDefect = defectRemarks;
                                    let qaFiles: string[] = [];

                                    if (defectRemarks.toLowerCase().includes("file:")) {
                                        const fIdx = defectRemarks.toLowerCase().indexOf("file:");
                                        const filePart = defectRemarks.substring(fIdx + 5).trim();
                                        let textPart = defectRemarks.substring(0, fIdx).trim();
                                        if (textPart.endsWith("|")) {
                                            textPart = textPart.substring(0, textPart.length - 1).trim();
                                        }
                                        cleanQaDefect = textPart;
                                        qaFiles = filePart.split(",").map((f: string) => f.trim()).filter(Boolean);
                                    }

                                    // Parse Production Action & proofs
                                    let cleanProdAction = prodRemark;
                                    let prodFiles: string[] = [];
                                    if (prodRemark.includes("| File:")) {
                                        const parts = prodRemark.split("| File:");
                                        cleanProdAction = parts[0].trim();
                                        prodFiles = parts[1].split(",").map((f: string) => f.trim()).filter(Boolean);
                                    }

                                    return (
                                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px' }}>{idx + 1}</td>
                                            <td style={{ padding: '10px', textAlign: 'left' }}>
                                                <strong style={{ color: '#0f172a' }}>{cp.cr3ea_area}</strong>
                                                <div style={{ color: '#475569', fontSize: '13px', marginTop: '2px' }}>{cp.cr3ea_criteria}</div>

                                                {/* QA Defect Box */}
                                                <div style={{ marginTop: '5px', fontSize: '11px', color: '#991b1b', backgroundColor: '#fef2f2', padding: '4px 8px', borderRadius: '4px', border: '1px solid #fee2e2' }}>
                                                    <strong>QA Defect:</strong> {cleanQaDefect || "Defect noted"}
                                                    {qaFiles.length > 0 && (
                                                        <span style={{ marginLeft: '6px' }}>({qaFiles.length} photo attached)</span>
                                                    )}
                                                </div>

                                                {/* Production Action Box */}
                                                <div style={{ marginTop: '5px', fontSize: '11px' }}>
                                                    {isReady ? (
                                                        <div style={{ backgroundColor: '#f0fdf4', padding: '4px 8px', borderRadius: '4px', border: '1px solid #dcfce7', color: '#166534' }}>
                                                            <strong>Production Action:</strong> {cleanProdAction}
                                                            {prodFiles.length > 0 && (
                                                                <span style={{ marginLeft: '6px', fontWeight: 600 }}>({prodFiles.length} proof photo attached)</span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span style={{ backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: '4px', fontWeight: 500, fontSize: '11px' }}>
                                                            Waiting for Production Action
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td style={{ padding: '10px' }}>
                                                <select
                                                    className="form-select"
                                                    value={scoreVal}
                                                    disabled={!isReady || state.isReadOnly}
                                                    onChange={(e) => handleScoreChange(rowKey, e.target.value)}
                                                    style={{ width: '100%', padding: '6px 10px', borderRadius: '4px', fontSize: '13px', borderColor: '#cbd5e1' }}
                                                >
                                                    <option value="">Select</option>
                                                    <option value="Compliant (2)">Compliant</option>
                                                    <option value="Partial (1)">Partial</option>
                                                    <option value="Non-Compliant (0)">Non-Compliant</option>
                                                </select>
                                            </td>

                                            <td style={{ padding: '10px' }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Enter verification remarks"
                                                    value={remarksVal}
                                                    disabled={!isReady || state.isReadOnly}
                                                    onChange={(e) => handleRemarksChange(rowKey, e.target.value)}
                                                    style={{ width: '100%', padding: '6px 10px', borderRadius: '4px', fontSize: '13px', borderColor: '#cbd5e1' }}
                                                />
                                            </td>

                                            <td style={{ padding: '10px' }}>
                                                <div className="custom-file-upload" style={{ border: '1px dashed #cbd5e1', padding: '6px', borderRadius: '4px', backgroundColor: isReady ? '#ffffff' : '#f8fafc' }}>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        multiple
                                                        disabled={!isReady || state.isReadOnly}
                                                        onChange={(e) => handleFileChange(rowKey, e.target.files)}
                                                        style={{ fontSize: '11px', width: '100%' }}
                                                    />

                                                    {/* Selected file chips */}
                                                    {files.length > 0 && (
                                                        <div style={{ marginTop: '4px', textAlign: 'left' }}>
                                                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#15803d' }}>
                                                                {files.length} photo(s) selected:
                                                            </div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                                                                {files.map((file, fIdx) => (
                                                                    <div
                                                                        key={fIdx}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            backgroundColor: '#f0fdf4',
                                                                            border: '1px solid #bbf7d0',
                                                                            padding: '2px 5px',
                                                                            borderRadius: '3px',
                                                                            fontSize: '11px',
                                                                            color: '#166534'
                                                                        }}
                                                                    >
                                                                        <span style={{ maxWidth: '85px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                                                                            {file.name}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemoveFile(rowKey, fIdx)}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                color: '#991b1b',
                                                                                cursor: 'pointer',
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            &times;
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {files.length === 0 && (
                                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
                                                            No image uploaded
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Bottom Button */}
            <div
                className="tour-cyle-btn-wrapper"
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '25px 0'
                }}
            >
                <button
                    type="button"
                    id="btn-submit-reverification"
                    onClick={handleSubmit}
                    disabled={state.isLoading || state.isReadOnly || failedCheckpoints.length === 0}
                    style={{
                        padding: '10px 28px',
                        backgroundColor: '#16a34a',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    {state.isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status"></span> Submitting Re-verification...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-check-circle"></i> Submit Re-Verification &amp; Finalize
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ALCQAReverify;
